import { action, computed, observable } from 'mobx'
import { firstError } from '@/features/legends/editor/validation'
import type { QuestionEditorViewModelBase } from './QuestionEditorViewModelBase'
import {
  type AnyQuestionEditor,
  asAnyQuestionEditor,
} from './question-registry'

export type PropertyInspectorTab = 'General' | 'Conditions' | 'Effects'

/**
 * The question selected for editing in the inspector, and its tab.
 */
export class PropertyInspectorViewModel {
  @observable.ref accessor questionVm: AnyQuestionEditor | null = null
  @observable accessor currentTab: PropertyInspectorTab = 'General'

  /**
   * Whether the selected question can't be edited, or there is none.
   */
  @computed
  get isEditingDisabled() {
    return this.questionVm?.isEditingDisabled ?? true
  }

  /**
   * The first error of the selected question the tab shows, if any: any error
   * for the General tab, its or its options' conditions, or its effects.
   *
   * @param tab
   */
  getTabError(tab: PropertyInspectorTab) {
    const question = this.questionVm
    if (!question) {
      return undefined
    }
    switch (tab) {
      case 'General':
        return firstError(question.validation)
      case 'Conditions':
        return (
          firstError(question.triggerWhen.validation) ??
          question.triggerWhenDescriptors
            ?.map((d) => firstError(d.triggerWhen.validation))
            .find(Boolean)
        )
      case 'Effects':
        return question.effectDescriptors
          ?.map((d) => firstError(d.effect.validation))
          .find(Boolean)
    }
  }

  /**
   * Switches the inspector's tab.
   */
  @action.bound
  setTab(tab: PropertyInspectorTab) {
    this.currentTab = tab
  }

  /**
   * Edits the question.
   */
  @action.bound
  select(question: QuestionEditorViewModelBase) {
    this.questionVm = asAnyQuestionEditor(question)
  }

  /**
   * Edits nothing.
   */
  @action.bound
  deselect() {
    this.questionVm = null
  }
}
